import { useState, useEffect, useRef } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  Image as ImageIcon,
  File as FileIcon,
  MapPin,
  Phone,
  Plus,
  Trash2,
  AlertCircle,
  Check,
  Upload
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import WhatsAppPreview from './WhatsAppPreview';
import FormattingToolbar from '../common/FormattingToolbar';
import EmojiPickerButton from '../common/EmojiPickerButton';

// Tipos
type TemplateCategory = 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
type HeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
type ButtonType = 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: HeaderFormat;
  text?: string;
  example?: {
    header_text?: string[];
    header_handle?: string[]; // URL para mídia (IMAGE, VIDEO, DOCUMENT)
    body_text?: string[][];
  };
  buttons?: TemplateButton[];
}

interface TemplateButton {
  type: ButtonType;
  text: string;
  phone_number?: string;
  url?: string;
}

interface CreateWhatsAppTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  wabaAccountId: string;
  onSuccess: () => void;
}

type WizardStep = 'basic' | 'header' | 'body' | 'footer' | 'buttons' | 'review';

export default function CreateWhatsAppTemplateModal({
  isOpen,
  onClose,
  wabaAccountId,
  onSuccess
}: CreateWhatsAppTemplateModalProps) {
  const { accessToken } = useAuth();
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successFeedback, setSuccessFeedback] = useState<string | null>(null);

  // Dados do template
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('pt_BR');
  const [category, setCategory] = useState<TemplateCategory>('UTILITY');
  const [allowCategoryChange, setAllowCategoryChange] = useState(false);

  // Componentes
  const [hasHeader, setHasHeader] = useState(false);
  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('TEXT');
  const [headerText, setHeaderText] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState(''); // URL para IMAGE, VIDEO, DOCUMENT
  const [mediaUrlStatus, setMediaUrlStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [mediaUrlError, setMediaUrlError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [bodyText, setBodyText] = useState('');
  const [bodyExamples, setBodyExamples] = useState<string[]>(['']);

  const [hasFooter, setHasFooter] = useState(false);
  const [footerText, setFooterText] = useState('');

  const [hasButtons, setHasButtons] = useState(false);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  // Formatting toolbar state
  const [showEmojiPicker, setShowEmojiPicker] = useState<'header' | 'body' | 'footer' | null>(null);
  const headerTextRef = useRef<HTMLInputElement>(null);
  const bodyTextRef = useRef<HTMLTextAreaElement>(null);
  const footerTextRef = useRef<HTMLInputElement>(null);

  // Validação em tempo real para variáveis no início/fim
  const [headerValidationError, setHeaderValidationError] = useState<string | null>(null);
  const [bodyValidationError, setBodyValidationError] = useState<string | null>(null);
  const [footerValidationError, setFooterValidationError] = useState<string | null>(null);
  const [buttonValidationErrors, setButtonValidationErrors] = useState<Record<number, string>>({});

  // Reset quando fechar
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('basic');
      setName('');
      setLanguage('pt_BR');
      setCategory('UTILITY');
      setAllowCategoryChange(false);
      setHasHeader(false);
      setHeaderFormat('TEXT');
      setHeaderText('');
      setHeaderExample('');
      setBodyText('');
      setBodyExamples(['']);
      setHasFooter(false);
      setFooterText('');
      setHasButtons(false);
      setButtons([]);
      setError(null);
      setSuccessFeedback(null);
      setHeaderValidationError(null);
      setBodyValidationError(null);
      setButtonValidationErrors({});
    }
  }, [isOpen]);

  // Validar URL de mídia com debounce
  useEffect(() => {
    if (!headerMediaUrl || !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)) {
      setMediaUrlStatus('idle');
      setMediaUrlError('');
      return;
    }

    const timeoutId = setTimeout(() => {
      validateMediaUrl(headerMediaUrl);
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
  }, [headerMediaUrl, headerFormat]);

  // Validação do nome do template
  const validateTemplateName = (value: string): boolean => {
    const regex = /^[a-z0-9_]+$/;
    return regex.test(value);
  };

  // Contar parâmetros no texto
  const countParameters = (text: string): number => {
    const matches = text.match(/\{\{(\d+)\}\}/g);
    return matches ? matches.length : 0;
  };

  // Validar URL de mídia em tempo real
  const validateMediaUrl = async (url: string) => {
    setMediaUrlError('');
    setMediaUrlStatus('idle');

    if (!url.trim()) {
      return;
    }

    // Validar que não é base64
    if (url.startsWith('data:')) {
      setMediaUrlStatus('invalid');
      setMediaUrlError('A URL da mídia deve ser uma URL pública (https://...), não base64. Faça upload da imagem em um servidor e use a URL pública.');
      return;
    }

    // Validar que não é URL de serviços não-públicos
    const blockedDomains = [
      'chat.google.com',
      'drive.google.com',
      'docs.google.com',
      'mail.google.com',
      'localhost',
      '127.0.0.1',
      '192.168.',
      '10.0.',
      '172.16.'
    ];

    const lowerUrl = url.toLowerCase();
    for (const domain of blockedDomains) {
      if (lowerUrl.includes(domain)) {
        setMediaUrlStatus('invalid');
        setMediaUrlError(
          `URLs do ${domain.includes('.') ? domain.split('.')[0] : 'localhost/redes locais'} não são acessíveis publicamente. ` +
          `Use um serviço de hospedagem como: Imgur (imgur.com), CloudFlare Images, AWS S3, ou seu próprio servidor público.`
        );
        return;
      }
    }

    // Validar que é uma URL válida
    try {
      new URL(url);
    } catch {
      setMediaUrlStatus('invalid');
      setMediaUrlError('URL inválida. Use uma URL pública completa (ex: https://exemplo.com/imagem.jpg)');
      return;
    }

    // Validar que começa com https
    if (!url.startsWith('https://')) {
      setMediaUrlStatus('invalid');
      setMediaUrlError('A URL deve usar HTTPS (https://...) para ser aceita pelo WhatsApp.');
      return;
    }

    // Validar que a URL é acessível e retorna o tipo correto
    setMediaUrlStatus('validating');

    try {
      // Tentar fazer requisição com cors habilitado primeiro
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      // Verificar se a resposta é bem-sucedida
      if (!response.ok) {
        setMediaUrlStatus('invalid');
        setMediaUrlError(`Erro ao acessar URL (HTTP ${response.status}). A URL precisa retornar status 200 e ser publicamente acessível.`);
        return;
      }

      // Verificar o Content-Type
      const contentType = response.headers.get('content-type');

      if (contentType) {
        const isValidType =
          (headerFormat === 'IMAGE' && contentType.startsWith('image/')) ||
          (headerFormat === 'VIDEO' && contentType.startsWith('video/')) ||
          (headerFormat === 'DOCUMENT' && (contentType.includes('pdf') || contentType.includes('document')));

        if (!isValidType) {
          setMediaUrlStatus('invalid');
          setMediaUrlError(
            `O servidor retornou tipo "${contentType}" mas esperávamos "${headerFormat}". ` +
            `Certifique-se de que a URL aponta diretamente para o arquivo ${headerFormat === 'IMAGE' ? 'de imagem (jpg, png, etc)' : headerFormat === 'VIDEO' ? 'de vídeo (mp4, etc)' : 'PDF/documento'}.`
          );
          return;
        }
      }

      setMediaUrlStatus('valid');
    } catch (error) {
      // Se falhar com CORS, tentar verificar de outra forma
      try {
        // Criar elemento de imagem para verificar se carrega (só funciona para imagens)
        if (headerFormat === 'IMAGE') {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
            // Timeout de 10 segundos
            setTimeout(() => reject(new Error('Timeout')), 10000);
          });
          setMediaUrlStatus('valid');
        } else {
          // Para vídeo e documento, apenas validar que não deu erro de CORS
          setMediaUrlStatus('invalid');
          setMediaUrlError(
            'Não foi possível validar a URL devido a restrições de CORS. ' +
            'Certifique-se de que: 1) A URL é pública e acessível, 2) O servidor permite requisições de outros domínios (CORS), ' +
            '3) A URL aponta diretamente para o arquivo (não uma página de visualização).'
          );
        }
      } catch (imgError) {
        setMediaUrlStatus('invalid');
        setMediaUrlError(
          'Não foi possível carregar a mídia. Verifique se: 1) A URL está correta, ' +
          '2) O arquivo existe e é acessível publicamente, 3) Não há bloqueios de CORS ou autenticação.'
        );
      }
    }
  };

  // Upload de arquivo para MinIO
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const validMimeTypes: Record<HeaderFormat, string[]> = {
      'IMAGE': ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      'VIDEO': ['video/mp4', 'video/mpeg', 'video/quicktime'],
      'DOCUMENT': ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      'TEXT': [],
      'LOCATION': []
    };

    const allowedTypes = validMimeTypes[headerFormat];
    if (!allowedTypes.includes(file.type)) {
      setMediaUrlError(`Tipo de arquivo inválido. Tipos aceitos: ${allowedTypes.join(', ')}`);
      setMediaUrlStatus('invalid');
      return;
    }

    // Validar tamanho
    const maxSizes: Record<HeaderFormat, number> = {
      'IMAGE': 5 * 1024 * 1024, // 5MB
      'VIDEO': 16 * 1024 * 1024, // 16MB
      'DOCUMENT': 100 * 1024 * 1024, // 100MB
      'TEXT': 0,
      'LOCATION': 0
    };

    const maxSize = maxSizes[headerFormat];
    if (file.size > maxSize) {
      setMediaUrlError(`Arquivo muito grande. Tamanho máximo: ${(maxSize / 1024 / 1024).toFixed(0)}MB`);
      setMediaUrlStatus('invalid');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setMediaUrlError('');
    setMediaUrlStatus('idle');

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Determinar endpoint baseado no tipo
      const endpoint = headerFormat === 'IMAGE' ? 'image' : headerFormat === 'VIDEO' ? 'video' : 'document';

      if (!accessToken) {
        throw new Error('Você precisa estar autenticado para fazer upload');
      }

      const response = await fetch(`${API_BASE_URL}/api/storage/upload/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao fazer upload');
      }

      const result = await response.json();

      // Auto-preencher URL
      setHeaderMediaUrl(result.data.url);
      setUploadProgress(100);
      setMediaUrlStatus('valid');

      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      setMediaUrlError(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
      setMediaUrlStatus('invalid');
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  // Validar se variáveis estão no início ou fim do texto (retorna mensagem de erro ou null)
  const checkLeadingTrailingParams = (text: string): string | null => {
    if (!text.trim()) return null;

    const trimmedText = text.trim();

    // Regex para variáveis: {{1}}, {{2}}, etc
    const startsWithParam = /^\{\{\d+\}\}/.test(trimmedText);
    const endsWithParam = /\{\{\d+\}\}$/.test(trimmedText);

    if (startsWithParam && endsWithParam) {
      return '❌ Variáveis NÃO PODEM estar no início E no fim do texto. Adicione texto antes e depois das variáveis.';
    }

    if (startsWithParam) {
      const match = trimmedText.match(/^(\{\{\d+\}\})/);
      return `❌ Variável ${match?.[1]} NÃO PODE estar no início do texto. Adicione texto antes. Ex: "Olá ${match?.[1]}, ..."`;
    }

    if (endsWithParam) {
      const match = trimmedText.match(/(\{\{\d+\}\})$/);
      return `❌ Variável ${match?.[1]} NÃO PODE estar no final do texto. Adicione texto depois. Ex: "... ${match?.[1]}!"`;
    }

    return null;
  };

  // Validar regras do WhatsApp para corpo de mensagem
  const checkBodyTextRules = (text: string): string | null => {
    if (!text.trim()) return null;

    // 1. Verificar quebras de linha consecutivas (máx 2)
    if (/\n{3,}/.test(text)) {
      return '❌ O corpo não pode ter mais de 2 quebras de linha consecutivas. O WhatsApp rejeita templates com 3+ quebras seguidas.';
    }

    // 2. Contar emojis (máx 10)
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{231A}-\u{231B}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}\u{25AA}-\u{25AB}\u{25B6}\u{25C0}\u{25FB}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708}-\u{270D}\u{270F}]/gu;
    const emojiMatches = text.match(emojiRegex);
    const emojiCount = emojiMatches ? emojiMatches.length : 0;
    if (emojiCount > 10) {
      return `❌ O corpo tem ${emojiCount} emojis. O WhatsApp permite no máximo 10 emojis por template.`;
    }

    // 3. Verificar se tem apenas parâmetros (sem texto real)
    const textWithoutParams = text.replace(/\{\{\d+\}\}/g, '').trim();
    const textWithoutFormatting = textWithoutParams.replace(/[*_~`\n\s]/g, '');
    if (textWithoutFormatting.length === 0 && /\{\{\d+\}\}/.test(text)) {
      return '❌ O corpo não pode conter apenas variáveis. Adicione texto real entre as variáveis.';
    }

    return null;
  };

  // Validar texto do botão (WhatsApp não permite variáveis, formatação, quebras de linha ou emojis)
  const validateButtonText = (text: string): { valid: boolean; error: string } => {
    if (!text.trim()) {
      return { valid: false, error: 'Texto do botão é obrigatório' };
    }

    // Variáveis {{1}}, {{2}}, etc
    if (/\{\{\d+\}\}/.test(text)) {
      return { valid: false, error: 'Botões não podem conter variáveis ({{1}}, {{2}}, etc.)' };
    }

    // Formatação: *negrito*, _itálico_, ~tachado~, `código`
    if (/[*_~`]/.test(text)) {
      return { valid: false, error: 'Botões não podem conter caracteres de formatação (*, _, ~, `)' };
    }

    // Quebras de linha
    if (/[\n\r]/.test(text)) {
      return { valid: false, error: 'Botões não podem conter quebras de linha' };
    }

    // Emojis (regex abrangente para detectar emojis Unicode)
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{231A}-\u{231B}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}\u{25AA}-\u{25AB}\u{25B6}\u{25C0}\u{25FB}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708}-\u{270D}\u{270F}]/u;
    if (emojiRegex.test(text)) {
      return { valid: false, error: 'Botões não podem conter emojis' };
    }

    return { valid: true, error: '' };
  };

  // Validação em tempo real do headerText
  useEffect(() => {
    if (hasHeader && headerFormat === 'TEXT' && headerText.trim()) {
      // BLOQUEIO TEMPORÁRIO: Não permitir variáveis no header de texto
      const hasVariables = /\{\{\d+\}\}/.test(headerText);
      if (hasVariables) {
        setHeaderValidationError('❌ Variáveis no header de texto não são permitidas. Use header de mídia (IMAGEM/VÍDEO/DOCUMENTO) para conteúdo dinâmico.');
      } else {
        setHeaderValidationError(null);
      }
    } else {
      setHeaderValidationError(null);
    }
  }, [headerText, hasHeader, headerFormat]);

  // Validação em tempo real do bodyText
  useEffect(() => {
    if (bodyText.trim()) {
      // Validar leading/trailing params
      const leadingTrailingError = checkLeadingTrailingParams(bodyText);
      if (leadingTrailingError) {
        setBodyValidationError(leadingTrailingError);
        return;
      }

      // Validar regras do WhatsApp
      const rulesError = checkBodyTextRules(bodyText);
      if (rulesError) {
        setBodyValidationError(rulesError);
        return;
      }

      setBodyValidationError(null);
    } else {
      setBodyValidationError(null);
    }
  }, [bodyText]);

  // Validação em tempo real do footerText
  useEffect(() => {
    if (hasFooter && footerText.trim()) {
      // BLOQUEIO TOTAL: Não permitir variáveis no footer
      const hasVariables = /\{\{\d+\}\}/.test(footerText);
      if (hasVariables) {
        setFooterValidationError('❌ Variáveis no rodapé não são permitidas. Rodapé deve ser texto estático.');
      } else {
        setFooterValidationError(null);
      }
    } else {
      setFooterValidationError(null);
    }
  }, [footerText, hasFooter]);

  // Validar se variáveis estão no início ou fim do texto (para uso no validateCurrentStep)
  const validateNoLeadingTrailingParams = (text: string, fieldName: string): boolean => {
    if (!text.trim()) return true;

    const error = checkLeadingTrailingParams(text);
    if (error) {
      setError(error);
      return false;
    }

    return true;
  };

  // Validar step atual
  const validateCurrentStep = (): boolean => {
    setError(null);

    switch (currentStep) {
      case 'basic':
        if (!name.trim()) {
          setError('Nome do template é obrigatório');
          return false;
        }
        if (!validateTemplateName(name)) {
          setError('Nome deve conter apenas letras minúsculas, números e underscores');
          return false;
        }
        if (!language) {
          setError('Idioma é obrigatório');
          return false;
        }
        return true;

      case 'header':
        if (hasHeader && headerFormat === 'TEXT' && !headerText.trim()) {
          setError('Texto do cabeçalho é obrigatório quando cabeçalho está ativado');
          return false;
        }
        if (hasHeader && headerFormat === 'TEXT' && countParameters(headerText) > 0 && !headerExample.trim()) {
          setError('Exemplo é obrigatório quando o cabeçalho contém parâmetros');
          return false;
        }
        // Validar variáveis no início/fim
        if (hasHeader && headerFormat === 'TEXT' && headerText.trim()) {
          if (!validateNoLeadingTrailingParams(headerText, 'Cabeçalho')) {
            return false;
          }
        }
        if (hasHeader && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)) {
          if (!headerMediaUrl.trim()) {
            setError('URL da mídia é obrigatória para cabeçalhos com imagem, vídeo ou documento');
            return false;
          }
          // Validar que não é base64
          if (headerMediaUrl.startsWith('data:')) {
            setError('A URL da mídia deve ser uma URL pública (https://...), não base64. Faça upload da imagem em um servidor e use a URL pública.');
            return false;
          }
          // Validar que é uma URL válida
          try {
            new URL(headerMediaUrl);
          } catch {
            setError('URL da mídia inválida. Use uma URL pública completa (ex: https://exemplo.com/imagem.jpg)');
            return false;
          }
          // Validar que a URL passou na validação em tempo real
          if (mediaUrlStatus === 'validating') {
            setError('Aguarde... validando URL da mídia');
            return false;
          }
          if (mediaUrlStatus === 'invalid') {
            setError(mediaUrlError || 'URL da mídia inválida');
            return false;
          }
          if (mediaUrlStatus !== 'valid') {
            setError('Por favor, aguarde a validação da URL da mídia');
            return false;
          }
        }
        return true;

      case 'body':
        if (!bodyText.trim()) {
          setError('Corpo da mensagem é obrigatório');
          return false;
        }
        // Validar variáveis no início/fim
        if (!validateNoLeadingTrailingParams(bodyText, 'Corpo da mensagem')) {
          return false;
        }
        // Validar regras do WhatsApp para body
        const bodyRulesError = checkBodyTextRules(bodyText);
        if (bodyRulesError) {
          setError(bodyRulesError);
          return false;
        }
        const bodyParamCount = countParameters(bodyText);
        if (bodyParamCount > 0) {
          const validExamples = bodyExamples.filter(ex => ex.trim() !== '');
          if (validExamples.length < bodyParamCount) {
            setError(`São necessários ${bodyParamCount} exemplos para os parâmetros do corpo`);
            return false;
          }
        }
        return true;

      case 'footer':
        if (hasFooter && !footerText.trim()) {
          setError('Texto do rodapé é obrigatório quando rodapé está ativado');
          return false;
        }
        return true;

      case 'buttons':
        if (hasButtons && buttons.length === 0) {
          setError('Adicione pelo menos um botão ou desative a seção de botões');
          return false;
        }
        for (let i = 0; i < buttons.length; i++) {
          const button = buttons[i];

          // Validação do texto do botão (variáveis, formatação, emojis, quebras de linha)
          const validation = validateButtonText(button.text);
          if (!validation.valid) {
            setError(`Botão ${i + 1}: ${validation.error}`);
            return false;
          }

          if (button.type === 'PHONE_NUMBER' && !button.phone_number?.trim()) {
            setError(`Botão ${i + 1}: número de telefone é obrigatório`);
            return false;
          }
          if (button.type === 'URL' && !button.url?.trim()) {
            setError(`Botão ${i + 1}: URL é obrigatória`);
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  // Verificar se o step atual pode avançar (para desabilitar botão Próximo)
  const canProceedToNextStep = (): boolean => {
    switch (currentStep) {
      case 'header':
        return !headerValidationError;
      case 'body':
        return !bodyValidationError;
      case 'buttons':
        return Object.keys(buttonValidationErrors).length === 0;
      default:
        return true;
    }
  };

  // Navegar entre steps
  const goToNextStep = () => {
    if (!validateCurrentStep()) return;

    const steps: WizardStep[] = ['basic', 'header', 'body', 'footer', 'buttons', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const goToPreviousStep = () => {
    const steps: WizardStep[] = ['basic', 'header', 'body', 'footer', 'buttons', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Adicionar botão
  const addButton = () => {
    if (buttons.length >= 10) {
      setError('Máximo de 10 botões permitidos');
      return;
    }
    setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }]);
  };

  // Remover botão
  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
    // Limpar erro de validação do botão removido e reindexar
    setButtonValidationErrors(prev => {
      const newErrors: Record<number, string> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const keyNum = parseInt(key);
        if (keyNum < index) {
          newErrors[keyNum] = value;
        } else if (keyNum > index) {
          newErrors[keyNum - 1] = value;
        }
      });
      return newErrors;
    });
  };

  // Atualizar botão
  const updateButton = (index: number, field: keyof TemplateButton, value: string) => {
    const newButtons = [...buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setButtons(newButtons);

    // Validação em tempo real para o campo text
    if (field === 'text') {
      const validation = validateButtonText(value);
      setButtonValidationErrors(prev => {
        const newErrors = { ...prev };
        if (!validation.valid) {
          newErrors[index] = validation.error;
        } else {
          delete newErrors[index];
        }
        return newErrors;
      });
    }
  };

  // Atualizar exemplo de parâmetro do body
  const updateBodyExample = (index: number, value: string) => {
    const newExamples = [...bodyExamples];
    newExamples[index] = value;
    setBodyExamples(newExamples);
  };

  // Formatting handlers
  const handleFormat = (
    field: 'header' | 'body' | 'footer',
    formatType: 'bold' | 'italic' | 'strikethrough' | 'code'
  ) => {
    const ref = field === 'header' ? headerTextRef : field === 'body' ? bodyTextRef : footerTextRef;
    const element = ref.current;
    if (!element) return;

    const start = element.selectionStart || 0;
    const end = element.selectionEnd || 0;
    const text = field === 'header' ? headerText : field === 'body' ? bodyText : footerText;
    const setText = field === 'header' ? setHeaderText : field === 'body' ? setBodyText : setFooterText;

    // Get format characters
    const formatChars: Record<typeof formatType, string> = {
      bold: '*',
      italic: '_',
      strikethrough: '~',
      code: '```'
    };
    const char = formatChars[formatType];

    // If there's a selection, wrap it
    if (start !== end) {
      const selectedText = text.substring(start, end);
      const newText = text.substring(0, start) + char + selectedText + char + text.substring(end);
      setText(newText);

      // Restore cursor position after state update
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start + char.length, end + char.length);
      }, 0);
    } else {
      // No selection, insert format at cursor
      const placeholder = 'texto';
      const newText = text.substring(0, start) + char + placeholder + char + text.substring(start);
      setText(newText);

      // Select the placeholder text
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start + char.length, start + char.length + placeholder.length);
      }, 0);
    }
  };

  const handleEmojiSelect = (field: 'header' | 'body' | 'footer', emoji: string) => {
    const ref = field === 'header' ? headerTextRef : field === 'body' ? bodyTextRef : footerTextRef;
    const element = ref.current;
    if (!element) return;

    const start = element.selectionStart || 0;
    const text = field === 'header' ? headerText : field === 'body' ? bodyText : footerText;
    const setText = field === 'header' ? setHeaderText : field === 'body' ? setBodyText : setFooterText;

    const newText = text.substring(0, start) + emoji + text.substring(start);
    setText(newText);

    // Move cursor after emoji
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);

    // Close emoji picker
    setShowEmojiPicker(null);
  };

  // Submeter template
  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const components: TemplateComponent[] = [];

      // Header
      if (hasHeader) {
        if (headerFormat === 'TEXT' && headerText.trim()) {
          const headerComponent: TemplateComponent = {
            type: 'HEADER',
            format: headerFormat,
            text: headerText
          };

          if (countParameters(headerText) > 0 && headerExample.trim()) {
            headerComponent.example = {
              header_text: [headerExample]
            };
          }

          components.push(headerComponent);
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && headerMediaUrl.trim()) {
          // Header com mídia
          const headerComponent: TemplateComponent = {
            type: 'HEADER',
            format: headerFormat,
            example: {
              header_handle: [headerMediaUrl]
            }
          };

          components.push(headerComponent);
        }
      }

      // Body (obrigatório)
      const bodyComponent: TemplateComponent = {
        type: 'BODY',
        text: bodyText
      };

      const bodyParamCount = countParameters(bodyText);
      if (bodyParamCount > 0) {
        const validExamples = bodyExamples.filter(ex => ex.trim() !== '').slice(0, bodyParamCount);
        bodyComponent.example = {
          body_text: [validExamples]
        };
      }

      components.push(bodyComponent);

      // Footer
      if (hasFooter && footerText.trim()) {
        components.push({
          type: 'FOOTER',
          text: footerText
        });
      }

      // Buttons
      if (hasButtons && buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: buttons.map(btn => ({
            type: btn.type,
            text: btn.text,
            ...(btn.type === 'PHONE_NUMBER' && { phone_number: btn.phone_number }),
            ...(btn.type === 'URL' && { url: btn.url })
          }))
        });
      }

      const payload = {
        wabaAccountId,
        name,
        language,
        category,
        allowCategoryChange,
        components
      };

      // DEBUG: Log payload antes de enviar
      console.log('[CreateTemplate] Payload sendo enviado:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${API_BASE_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Parse error codes and field information from backend
        const errorCode = errorData.data?.errorCode;
        const fieldType = errorData.data?.fieldType; // Ex: 'imagem', 'texto', 'vídeo'
        const fieldName = errorData.data?.fieldName; // Ex: 'Cabeçalho (Imagem)', 'Corpo da mensagem'
        const componentType = errorData.data?.componentType; // Ex: 'HEADER', 'BODY'

        // Use the backend message if available, otherwise construct one
        let friendlyMessage = errorData.message || 'Erro ao criar template';

        // Map specific error codes to user-friendly messages with field context
        if (errorCode === 'TEMPLATE_LEADING_TRAILING_PARAMS') {
          friendlyMessage = errorData.message || `❌ Erro de validação do WhatsApp:\n\nAs variáveis {{1}}, {{2}}, etc. NÃO PODEM estar no início ou fim do texto.\n\n❌ ERRADO:\n"{{1}} é o seu nome" (variável no início)\n"Seu nome é {{1}}" (variável no fim)\n\n✅ CORRETO:\n"Olá {{1}}, bem-vindo!" (variável no meio)\n"Obrigado, {{1}}!" (tem texto depois)\n\n💡 Adicione texto antes E depois de cada variável.`;
        } else if (errorCode === 'MEDIA_URL_FORBIDDEN') {
          const field = fieldName || `mídia (${fieldType})`;
          friendlyMessage = `❌ Erro no campo: ${field}\n\nA URL retornou erro 403 (Proibido). Verifique se a URL é pública e acessível sem autenticação.`;
        } else if (errorCode === 'MEDIA_URL_RATE_LIMITED') {
          const field = fieldName || `mídia (${fieldType})`;
          friendlyMessage = `❌ Erro no campo: ${field}\n\nA URL retornou erro 429 (Muitas requisições). O servidor está bloqueando por rate limiting. Use uma URL de um servidor mais confiável (ex: https://picsum.photos/800/600).`;
        } else if (errorCode === 'MEDIA_URL_UNREACHABLE') {
          const field = fieldName || `mídia (${fieldType})`;
          friendlyMessage = `❌ Erro no campo: ${field}\n\nNão foi possível acessar a URL. Verifique se a URL está correta e é publicamente acessível.`;
        } else if (errorCode === 'MEDIA_URL_INVALID_FORMAT') {
          const field = fieldName || `mídia (${fieldType})`;
          friendlyMessage = `❌ Erro no campo: ${field}\n\nA URL está em formato inválido. Use uma URL pública (https://...), não base64.`;
        } else if (errorCode === 'TEMPLATE_PARAMETER_ERROR') {
          friendlyMessage = `❌ Erro nos parâmetros do template\n\n${errorData.message || 'Verifique os parâmetros {{1}}, {{2}}, etc.'}`;
        } else if (errorCode === 'TEMPLATE_TEXT_ERROR') {
          const field = fieldName || 'conteúdo de texto';
          friendlyMessage = `❌ Erro no campo: ${field}\n\n${errorData.message || 'Verifique o texto do template.'}`;
        } else if (errorCode === 'TEMPLATE_ALREADY_EXISTS') {
          friendlyMessage = '❌ Template já existe\n\nJá existe um template com este nome e idioma. Escolha outro nome ou idioma.';
        } else if (errorCode === 'DIALOG360_CREATE_TEMPLATE_ERROR') {
          const field = fieldName || 'template';
          friendlyMessage = `❌ Erro ao criar template${field !== 'template' ? ` no campo: ${field}` : ''}\n\n${errorData.message || 'Erro desconhecido. Verifique os dados e tente novamente.'}`;
        }

        throw new Error(friendlyMessage);
      }

      const result = await response.json();

      // Show success message with better formatting
      const statusMessage = result.data?.template?.status || 'PENDING';
      const statusLabel = statusMessage === 'PENDING' ?
        'Aguardando aprovação do Meta (até 24h)' :
        statusMessage;

      // Check if there's a category change warning from backend
      const categoryWarning = result.data?.template?._warning;

      let successMessage = `✅ SUCESSO! Template criado!\n\n` +
        `Nome: ${name}\n` +
        `Status: ${statusLabel}\n\n` +
        `✓ O template foi salvo no banco de dados com sucesso!\n` +
        `✓ Ele já está disponível na lista de templates\n` +
        `${statusMessage === 'PENDING' ? '\n⏳ Aguardando aprovação do Meta (pode levar até 24 horas)' : ''}`;

      // Add category change warning if present
      if (categoryWarning) {
        successMessage += `\n\n${categoryWarning}`;
      }

      setSuccessFeedback(successMessage);

      // Call onSuccess to reload templates and close modal after a brief delay
      // so the user can see the success message
      setTimeout(async () => {
        await onSuccess();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Renderizar step atual
  const renderStep = () => {
    switch (currentStep) {
      case 'basic':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-1 mb-1">
                Nome do Template *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                placeholder="ex: boas_vindas_cliente"
                className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
              />
              <p className="text-xs text-text-3 mt-1">
                Apenas letras minúsculas, números e underscores (_)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-1 mb-1">
                Idioma *
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
              >
                <option value="pt_BR">Português (Brasil)</option>
                <option value="en_US">English (US)</option>
                <option value="es_ES">Español (España)</option>
                <option value="es_MX">Español (México)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-1 mb-1">
                Categoria *
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
              >
                <option value="UTILITY">Utilidade (Notificações, alertas)</option>
                <option value="MARKETING">Marketing (Promoções, ofertas)</option>
                <option value="AUTHENTICATION">Autenticação (Códigos, senhas)</option>
              </select>
              <p className="text-xs text-text-3 mt-1">
                {category === 'MARKETING' && 'Requer opt-in do usuário. Limitado a 1 mensagem/24h.'}
                {category === 'UTILITY' && 'Para notificações transacionais e updates.'}
                {category === 'AUTHENTICATION' && 'Para códigos de verificação e autenticação.'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allowCategoryChange"
                checked={allowCategoryChange}
                onChange={(e) => setAllowCategoryChange(e.target.checked)}
                className="rounded border-bg-3"
              />
              <label htmlFor="allowCategoryChange" className="text-sm text-text-2">
                Permitir que Meta mude a categoria durante revisão
              </label>
            </div>
          </div>
        );

      case 'header':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasHeader"
                checked={hasHeader}
                onChange={(e) => setHasHeader(e.target.checked)}
                className="rounded border-bg-3"
              />
              <label htmlFor="hasHeader" className="text-sm font-medium text-text-1">
                Adicionar Cabeçalho (Opcional)
              </label>
            </div>

            {hasHeader && (
              <>
                <div>
                  <label className="block text-sm font-medium text-text-1 mb-1">
                    Formato do Cabeçalho
                  </label>
                  <select
                    value={headerFormat}
                    onChange={(e) => setHeaderFormat(e.target.value as HeaderFormat)}
                    className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                  >
                    <option value="TEXT">TEXTO</option>
                    <option value="IMAGE">IMAGEM</option>
                    {/* VIDEO, DOCUMENT e LOCATION: Em desenvolvimento futuro */}
                  </select>
                </div>

                {headerFormat === 'TEXT' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-text-1 mb-1">
                        Texto do Cabeçalho *
                      </label>
                      <div className="space-y-2">
                        <FormattingToolbar
                          onFormat={(formatType) => handleFormat('header', formatType)}
                          onEmojiClick={() => setShowEmojiPicker(showEmojiPicker === 'header' ? null : 'header')}
                        />
                        <div className="relative">
                          <input
                            ref={headerTextRef}
                            type="text"
                            value={headerText}
                            onChange={(e) => setHeaderText(e.target.value)}
                            placeholder="ex: Promoção Especial!"
                            maxLength={60}
                            className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                          />
                          <EmojiPickerButton
                            isOpen={showEmojiPicker === 'header'}
                            onToggle={() => setShowEmojiPicker(showEmojiPicker === 'header' ? null : 'header')}
                            onEmojiSelect={(emoji) => handleEmojiSelect('header', emoji)}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-text-3 mt-1">
                        Máximo 60 caracteres. Para conteúdo dinâmico, use header de mídia (IMAGEM/VÍDEO/DOCUMENTO).
                      </p>
                      {headerValidationError && (
                        <p className="text-xs text-danger font-medium mt-2">
                          {headerValidationError}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {headerFormat !== 'TEXT' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-text-1 mb-2">
                        URL Pública da Mídia ({headerFormat === 'IMAGE' ? 'Imagem' : headerFormat === 'VIDEO' ? 'Vídeo' : 'Documento'}) *
                      </label>

                      {/* Upload Button */}
                      <div className="mb-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept={
                            headerFormat === 'IMAGE' ? 'image/jpeg,image/jpg,image/png,image/gif,image/webp' :
                            headerFormat === 'VIDEO' ? 'video/mp4,video/mpeg,video/quicktime' :
                            'application/pdf,.doc,.docx,.xls,.xlsx'
                          }
                          onChange={handleFileUpload}
                          disabled={isUploading}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 disabled:bg-bg-3 disabled:cursor-not-allowed text-white rounded-md transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          {isUploading ? 'Fazendo upload...' : `Fazer upload de ${headerFormat === 'IMAGE' ? 'imagem' : headerFormat === 'VIDEO' ? 'vídeo' : 'documento'}`}
                        </button>

                        {isUploading && uploadProgress > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-bg-3 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-accent-1 h-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-text-3 mt-1 text-center">{uploadProgress}%</p>
                          </div>
                        )}
                      </div>

                      {/* URL Input */}
                      <div className="relative">
                        <input
                          type="url"
                          className={`w-full px-3 py-2 pr-10 bg-bg-1 border rounded-md text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1 ${
                            mediaUrlStatus === 'valid' ? 'border-success' :
                            mediaUrlStatus === 'invalid' ? 'border-danger' :
                            mediaUrlStatus === 'validating' ? 'border-warning' :
                            'border-bg-3'
                          }`}
                          placeholder={`https://exemplo.com/${headerFormat === 'IMAGE' ? 'imagem.jpg' : headerFormat === 'VIDEO' ? 'video.mp4' : 'documento.pdf'}`}
                          value={headerMediaUrl}
                          onChange={(e) => setHeaderMediaUrl(e.target.value)}
                          disabled={isUploading}
                        />
                        {mediaUrlStatus === 'validating' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-warning border-t-transparent rounded-full"></div>
                          </div>
                        )}
                        {mediaUrlStatus === 'valid' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="h-5 w-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        {mediaUrlStatus === 'invalid' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <svg className="h-5 w-5 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {mediaUrlStatus === 'invalid' && mediaUrlError && (
                        <p className="text-xs text-danger mt-1 font-medium">
                          ❌ {mediaUrlError}
                        </p>
                      )}
                      {mediaUrlStatus === 'valid' && (
                        <p className="text-xs text-success mt-1 font-medium">
                          ✅ URL validada com sucesso
                        </p>
                      )}
                      {mediaUrlStatus === 'validating' && (
                        <p className="text-xs text-warning mt-1 font-medium">
                          🔄 Validando URL...
                        </p>
                      )}

                      <p className="text-xs text-text-3 mt-1">
                        💡 Faça upload do arquivo ou cole uma URL pública. A URL será validada automaticamente.
                      </p>

                      {/* Image Preview */}
                      {headerFormat === 'IMAGE' && headerMediaUrl && mediaUrlStatus === 'valid' && (
                        <div className="mt-3">
                          <p className="text-xs text-text-2 mb-2">Visualização:</p>
                          <div className="relative w-full max-w-sm mx-auto rounded-lg overflow-hidden border border-bg-3">
                            <img
                              src={headerMediaUrl}
                              alt="Visualização"
                              className="w-full h-auto max-h-64 object-contain bg-bg-2"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'body':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-1 mb-1">
                Corpo da Mensagem *
              </label>
              <div className="space-y-2">
                <FormattingToolbar
                  onFormat={(formatType) => handleFormat('body', formatType)}
                  onEmojiClick={() => setShowEmojiPicker(showEmojiPicker === 'body' ? null : 'body')}
                />
                <div className="relative">
                  <textarea
                    ref={bodyTextRef}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    placeholder="ex: Olá {{1}}, seu pedido {{2}} foi confirmado!"
                    rows={6}
                    maxLength={1024}
                    className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                  />
                  <EmojiPickerButton
                    isOpen={showEmojiPicker === 'body'}
                    onToggle={() => setShowEmojiPicker(showEmojiPicker === 'body' ? null : 'body')}
                    onEmojiSelect={(emoji) => handleEmojiSelect('body', emoji)}
                  />
                </div>
              </div>
              <p className="text-xs text-text-3 mt-1">
                Máximo 1024 caracteres. Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis.
              </p>
              {bodyValidationError && (
                <p className="text-xs text-danger font-medium mt-2">
                  {bodyValidationError}
                </p>
              )}
            </div>

            {countParameters(bodyText) > 0 && (
              <div>
                <label className="block text-sm font-medium text-text-1 mb-2">
                  Exemplos para Parâmetros *
                </label>
                <div className="space-y-2">
                  {Array.from({ length: countParameters(bodyText) }).map((_, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={bodyExamples[index] || ''}
                        onChange={(e) => updateBodyExample(index, e.target.value)}
                        placeholder={`Exemplo para {{${index + 1}}}`}
                        className="flex-1 px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'footer':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasFooter"
                checked={hasFooter}
                onChange={(e) => setHasFooter(e.target.checked)}
                className="rounded border-bg-3"
              />
              <label htmlFor="hasFooter" className="text-sm font-medium text-text-1">
                Adicionar Rodapé (Opcional)
              </label>
            </div>

            {hasFooter && (
              <div>
                <label className="block text-sm font-medium text-text-1 mb-1">
                  Texto do Rodapé *
                </label>
                <div className="space-y-2">
                  <FormattingToolbar
                    onFormat={(formatType) => handleFormat('footer', formatType)}
                    onEmojiClick={() => setShowEmojiPicker(showEmojiPicker === 'footer' ? null : 'footer')}
                  />
                  <div className="relative">
                    <input
                      ref={footerTextRef}
                      type="text"
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      placeholder="ex: Equipe de Atendimento"
                      maxLength={60}
                      className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                    />
                    <EmojiPickerButton
                      isOpen={showEmojiPicker === 'footer'}
                      onToggle={() => setShowEmojiPicker(showEmojiPicker === 'footer' ? null : 'footer')}
                      onEmojiSelect={(emoji) => handleEmojiSelect('footer', emoji)}
                    />
                  </div>
                </div>
                <p className="text-xs text-text-3 mt-1">
                  Máximo 60 caracteres. Variáveis não são permitidas no rodapé.
                </p>
                {footerValidationError && (
                  <p className="text-xs text-danger font-medium mt-2">
                    {footerValidationError}
                  </p>
                )}
              </div>
            )}
          </div>
        );

      case 'buttons':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasButtons"
                checked={hasButtons}
                onChange={(e) => setHasButtons(e.target.checked)}
                className="rounded border-bg-3"
              />
              <label htmlFor="hasButtons" className="text-sm font-medium text-text-1">
                Adicionar Botões (Opcional)
              </label>
            </div>

            {hasButtons && (
              <>
                <div className="space-y-3">
                  {buttons.map((button, index) => (
                    <div key={index} className="p-3 border border-bg-3 rounded-md space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-text-1">Botão {index + 1}</span>
                        <button
                          onClick={() => removeButton(index)}
                          className="text-danger hover:text-danger/80"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <select
                        value={button.type}
                        onChange={(e) => updateButton(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                      >
                        <option value="QUICK_REPLY">Resposta Rápida</option>
                        <option value="PHONE_NUMBER">Ligar</option>
                        <option value="URL">Abrir Link</option>
                      </select>

                      <div>
                        <input
                          type="text"
                          value={button.text}
                          onChange={(e) => updateButton(index, 'text', e.target.value)}
                          placeholder="Texto do botão"
                          maxLength={25}
                          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1 ${
                            buttonValidationErrors[index] ? 'border-danger' : 'border-bg-3'
                          }`}
                        />
                        {buttonValidationErrors[index] && (
                          <p className="text-xs text-danger mt-1">
                            {buttonValidationErrors[index]}
                          </p>
                        )}
                        <p className="text-xs text-text-3 mt-1">
                          Máx. 25 caracteres. Sem variáveis, emojis ou formatação.
                        </p>
                      </div>

                      {button.type === 'PHONE_NUMBER' && (
                        <input
                          type="tel"
                          value={button.phone_number || ''}
                          onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                          placeholder="ex: +5583999999999"
                          className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                        />
                      )}

                      {button.type === 'URL' && (
                        <input
                          type="url"
                          value={button.url || ''}
                          onChange={(e) => updateButton(index, 'url', e.target.value)}
                          placeholder="https://exemplo.com"
                          className="w-full px-3 py-2 border border-bg-3 rounded-md focus:ring-2 focus:ring-accent-1 focus:border-transparent bg-bg-2 text-text-1"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {buttons.length < 10 && (
                  <button
                    onClick={addButton}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-bg-3 rounded-md hover:border-accent-1 hover:bg-accent-1/5 transition-colors text-text-2"
                  >
                    <Plus size={16} />
                    Adicionar Botão
                  </button>
                )}
              </>
            )}
          </div>
        );

      case 'review':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-bg-3 rounded-md space-y-3">
              <h4 className="font-medium text-text-1">Revisão do Template</h4>

              <div>
                <p className="text-xs text-text-3">Nome</p>
                <p className="text-sm text-text-1">{name}</p>
              </div>

              <div>
                <p className="text-xs text-text-3">Idioma</p>
                <p className="text-sm text-text-1">{language}</p>
              </div>

              <div>
                <p className="text-xs text-text-3">Categoria</p>
                <p className="text-sm text-text-1">{category}</p>
              </div>

              {hasHeader && headerFormat === 'TEXT' && (
                <div>
                  <p className="text-xs text-text-3">Cabeçalho</p>
                  <p className="text-sm text-text-1">{headerText}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-text-3">Corpo</p>
                <p className="text-sm text-text-1 whitespace-pre-wrap">{bodyText}</p>
              </div>

              {hasFooter && (
                <div>
                  <p className="text-xs text-text-3">Rodapé</p>
                  <p className="text-sm text-text-1">{footerText}</p>
                </div>
              )}

              {hasButtons && buttons.length > 0 && (
                <div>
                  <p className="text-xs text-text-3 mb-1">Botões</p>
                  <div className="space-y-1">
                    {buttons.map((btn, idx) => (
                      <p key={idx} className="text-sm text-text-1">
                        {idx + 1}. {btn.text} ({btn.type})
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
              <p className="text-sm text-warning">
                <AlertCircle size={16} className="inline mr-1" />
                O template será enviado para aprovação da Meta. Isso pode levar até 24 horas.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Steps do wizard
  const steps: { key: WizardStep; label: string; icon: typeof MessageSquare }[] = [
    { key: 'basic', label: 'Básico', icon: MessageSquare },
    { key: 'header', label: 'Cabeçalho', icon: ImageIcon },
    { key: 'body', label: 'Corpo', icon: FileIcon },
    { key: 'footer', label: 'Rodapé', icon: MapPin },
    { key: 'buttons', label: 'Botões', icon: Phone },
    { key: 'review', label: 'Revisão', icon: Check }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex">
        {/* Preview à esquerda */}
        <div className="w-[400px] bg-bg-2 rounded-l-lg flex flex-col">
          <div className="p-4 border-b border-border-1">
            <h3 className="text-sm font-medium text-text-2">Preview da Mensagem</h3>
          </div>
          <div className="flex-1 overflow-hidden">
            <WhatsAppPreview
              headerFormat={hasHeader ? headerFormat : undefined}
              headerText={hasHeader && headerFormat === 'TEXT' ? headerText : undefined}
              headerExample={headerExample}
              headerMediaUrl={hasHeader && headerFormat !== 'TEXT' ? headerMediaUrl : undefined}
              bodyText={bodyText}
              bodyExamples={bodyExamples}
              footerText={hasFooter ? footerText : undefined}
              buttons={hasButtons ? buttons : []}
              highlightVariables
            />
          </div>
        </div>

        {/* Formulário à direita */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-bg-3">
            <h2 className="text-xl font-semibold text-text-1">Criar Template WhatsApp</h2>
            <button
              onClick={onClose}
              className="text-text-3 hover:text-text-1 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.key === currentStep;
                const isCompleted = index < currentStepIndex;

                return (
                  <div key={step.key} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive
                            ? 'bg-accent-1 text-white'
                            : isCompleted
                            ? 'bg-success text-white'
                            : 'bg-bg-3 text-text-3'
                        }`}
                      >
                        {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                      </div>
                      <p className={`text-xs mt-1 ${isActive ? 'text-accent-1 font-medium' : 'text-text-3'}`}>
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-0.5 flex-1 ${isCompleted ? 'bg-success' : 'bg-bg-3'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-md">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            {successFeedback && (
              <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-md">
                <p className="text-sm text-success whitespace-pre-line">{successFeedback}</p>
              </div>
            )}

            {renderStep()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-bg-3">
            <button
              onClick={goToPreviousStep}
              disabled={currentStep === 'basic' || isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-text-2 hover:text-text-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
              Voltar
            </button>

            {currentStep !== 'review' ? (
              <button
                onClick={goToNextStep}
                disabled={isSubmitting || !canProceedToNextStep()}
                className="flex items-center gap-2 px-6 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2 bg-success hover:bg-success/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Criando...' : 'Criar Template'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
